import { useState } from 'react';
import type { BookOverride, ClientBookMode, Location } from '@salonomy/types';
import { Button, Field, Modal, Pill, Select, TextInput } from '@salonomy/ui';
import { useInviteResidency } from '../../data/practitioner.js';
import type { InviteResidencyBody } from '../../data/practitioner.js';
import { ROLE_LABEL } from '../../lib/rbac.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS11 — invite an EXISTING-account practitioner to take a chair_renter residency in the current salon
 * (brief 11 §2; extends WS6's staff invite — StaffModal's vocabulary). The role is FIXED to chair_renter
 * here: a residency is, by definition, a renter membership (a fixed-seat hire of an outside professional),
 * so we don't expose the role picker the general team modal does. Their login is never re-credentialed —
 * the invite attaches to the human behind that email (data/practitioner.ts InviteResidencyBody).
 *
 * Owner/manager-only — the caller renders this only when authorised (ResidenciesPanel gates on rbac).
 * Book override mirrors StaffModal: it only has meaning in a `mixed` salon; in shared/sealed the salon's
 * mode decides, so we hide the control and label the effective stance honestly.
 */

const OVERRIDES: Array<{ value: BookOverride; label: string }> = [
  { value: 'inherit', label: 'Inherit salon default' },
  { value: 'shared', label: 'Shared (visible salon-wide)' },
  { value: 'sealed', label: 'Sealed (theirs alone)' },
];

export function InviteResidencyModal({
  locations,
  clientBookMode,
  onClose,
}: {
  locations: Location[];
  clientBookMode: ClientBookMode;
  onClose: () => void;
}) {
  const invite = useInviteResidency();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [title, setTitle] = useState('');
  const [locationId, setLocationId] = useState('');
  const [bookOverride, setBookOverride] = useState<BookOverride>('inherit');
  const [busy, setBusy] = useState(false);

  // The override only resolves to a choice in `mixed`; shared/sealed salons follow their salon-wide mode.
  const showOverride = clientBookMode === 'mixed';

  async function submit() {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error('Enter the practitioner’s existing-account email.');
      return;
    }
    setBusy(true);
    const body: InviteResidencyBody = {
      email: trimmedEmail,
      displayName: displayName.trim() || undefined,
      title: title.trim() || undefined,
      locationId: locationId || null,
      bookOverride,
    };
    try {
      await invite.mutateAsync(body);
      toast.success('Residency invite sent');
      onClose();
    } catch {
      toast.error(
        'Could not invite this practitioner. Check they already have a Salonomy account.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Invite a chair renter"
      width={520}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            Send invite
          </Button>
        </>
      }
    >
      <div
        className="muted"
        style={{
          fontSize: 12.5,
          background: 'var(--slm-porcelain)',
          border: '1px solid var(--slm-border)',
          borderRadius: 8,
          padding: '8px 10px',
          margin: '0 0 14px',
        }}
      >
        A residency invites a professional who <strong>already has a Salonomy account</strong> to
        rent a chair here — they keep their one login and their own client book. The seat is created
        as a <Pill tone="neutral">{ROLE_LABEL.chair_renter}</Pill>.
      </div>

      <Field
        label="Practitioner email"
        hint="Their existing Salonomy login — no new password is set."
      >
        <TextInput
          autoFocus
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
        />
      </Field>

      <Field label="Display name" hint="Optional — how they appear on your diary and roster.">
        <TextInput
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. Amara K."
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Location" hint={locations.length ? undefined : 'No locations yet.'}>
          <Select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">No specific location</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Title" hint='Free text, e.g. "Colour specialist".'>
          <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
      </div>

      {showOverride ? (
        <Field
          label="Client book"
          hint="This salon is in mixed mode — choose how this renter’s book behaves."
        >
          <Select
            value={bookOverride}
            onChange={(e) => setBookOverride(e.target.value as BookOverride)}
          >
            {OVERRIDES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      ) : (
        <div className="muted" style={{ fontSize: 12.5, margin: '2px 0 4px' }}>
          {clientBookMode === 'sealed'
            ? 'This salon seals every renter’s book — their clients will be theirs alone.'
            : 'This salon shares one book — the renter’s clients will be visible salon-wide.'}
        </div>
      )}
    </Modal>
  );
}
